import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { DefaultErrorMessage, TableQueryFilter } from "@/Contracts";
import {
	Client,
	ClientContact,
	ClientsRequest,
	AddClientContactRequest,
	AddClientRequest,
	ClientCount,
	ClientUserContactType,
	ClientContactRequest,
	ChildEntityOpportunities,
	ClientsResponse,
	EditClientRequest,
	ClientContactBulkUpdateResponse,
	ClientContactBulkUpdateRequest,
	ClientContactBulkUpdateServiceResponse
} from "@/Contracts/Clients";

import {
	getClients,
	getClientDetails,
	getClientContacts,
	deleteClientContact,
	addClientContact,
	editClientContact,
	deleteClient,
	addClient,
	editClient,
	getClientViewMetadata,
	getAllClientViews,
	createClientView,
	deleteClientView,
	getClientListPost,
	updateClientView,
	bulkDeleteClients,
	bulkUpdateClients,
	bulkUpdateClientContact,
	getClientsUsingGlobalSearch
} from "@/Services/Clients";
import { Opportunity } from "@/Contracts/Opportunity";
import {
	BulkDeleteRecordsRequest,
	BulkUpdateRecordsRequest,
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata,
	ListPagePostRequest
} from "@/Contracts/UI";
import { GlobalState } from "@/Reducers/interface";
import { ClientsGlobalSearchRequest } from "@/Contracts";
import { areFiltersChanged } from "@/Utils/common";

export enum ClientsActionTypes {
	SHOW_CLIENTS_LOADER = "SHOW_CLIENTS_LOADER",
	HIDE_CLIENTS_LOADER = "HIDE_CLIENTS_LOADER",
	CLIENTS_REQUESTED = "CLIENTS_REQUESTED",
	CLIENTS_SUCCESSFUL = "CLIENTS_SUCCESSFUL",
	CLIENTS_FAILED = "CLIENTS_FAILED",
	CLIENTS_RESET = "CLIENTS_RESET",
	CLIENT_DETAILS_REQUESTED = "CLIENT_DETAILS_REQUESTED",
	CLIENT_DETAILS_SUCCESSFUL = "CLIENT_DETAILS_SUCCESSFUL",
	CLIENT_DETAILS_FAILED = "CLIENT_DETAILS_FAILED",
	CLIENT_DETAILS_UPDATED = "CLIENT_DETAILS_UPDATED",
	CLIENT_DETAILS_RESET = "CLIENT_DETAILS_RESET",
	CLIENT_CONTACTS_REQUESTED = "CLIENT_CONTACTS_REQUESTED",
	CLIENT_CONTACTS_SUCCESSFUL = "CLIENT_CONTACTS_SUCCESSFUL",
	CLIENT_CONTACTS_FAILED = "CLIENT_CONTACTS_FAILED",
	CLIENT_CONTACTS_RESET = "CLIENT_CONTACTS_RESET",
	DELETE_CLIENT_CONTACT_REQUESTED = "DELETE_CLIENT_CONTACT_REQUESTED",
	DELETE_CLIENT_CONTACT_SUCCESSFUL = "DELETE_CLIENT_CONTACT_SUCCESSFUL",
	DELETE_CLIENT_CONTACT_FAILED = "DELETE_CLIENT_CONTACT_FAILED",
	ADD_CLIENT_CONTACT_REQUESTED = "ADD_CLIENT_CONTACT_REQUESTED",
	ADD_CLIENT_CONTACT_SUCCESSFUL = "ADD_CLIENT_CONTACT_SUCCESSFUL",
	ADD_CLIENT_CONTACT_FAILED = "ADD_CLIENT_CONTACT_FAILED",
	EDIT_CLIENT_CONTACT_REQUESTED = "EDIT_CLIENT_CONTACT_REQUESTED",
	EDIT_CLIENT_CONTACT_SUCCESSFUL = "EDIT_CLIENT_CONTACT_SUCCESSFUL",
	EDIT_CLIENT_CONTACT_FAILED = "EDIT_CLIENT_CONTACT_FAILED",
	DELETE_CLIENT_REQUESTED = "DELETE_CLIENT_REQUESTED",
	DELETE_CLIENT_SUCCESSFUL = "DELETE_CLIENT_SUCCESSFUL",
	DELETE_CLIENT_FAILED = "DELETE_CLIENT_FAILED",
	ADD_CLIENT_REQUESTED = "ADD_CLIENT_REQUESTED",
	ADD_CLIENT_SUCCESSFUL = "ADD_CLIENT_SUCCESSFUL",
	ADD_CLIENT_FAILED = "ADD_CLIENT_FAILED",
	EDIT_CLIENT_REQUESTED = "EDIT_CLIENT_REQUESTED",
	EDIT_CLIENT_SUCCESSFUL = "EDIT_CLIENT_SUCCESSFUL",
	EDIT_CLIENT_FAILED = "EDIT_CLIENT_FAILED",
	ADD_CLIENT_DRAWER_OPENED = "ADD_CLIENT_DRAWER_OPENED",
	ADD_CLIENT_DRAWER_CLOSED = "ADD_CLIENT_DRAWER_CLOSED",
	READONLY_CLIENT_DRAWER_OPENED = "READONLY_CLIENT_DRAWER_OPENED",
	READONLY_CLIENT_DRAWER_CLOSED = "READONLY_CLIENT_DRAWER_CLOSED",
	EDIT_CLIENT_DRAWER_OPENED = "EDIT_CLIENT_DRAWER_OPENED",
	EDIT_CLIENT_DRAWER_CLOSED = "EDIT_CLIENT_DRAWER_CLOSED",
	ADD_CLIENT_CONTACT_DRAWER_OPENED = "ADD_CLIENT_CONTACT_DRAWER_OPENED",
	ADD_CLIENT_CONTACT_DRAWER_CLOSED = "ADD_CLIENT_CONTACT_DRAWER_CLOSED",
	EDIT_CLIENT_CONTACT_DRAWER_OPENED = "EDIT_CLIENT_CONTACT_DRAWER_OPENED",
	EDIT_CLIENT_CONTACT_DRAWER_CLOSED = "EDIT_CLIENT_CONTACT_DRAWER_CLOSED",
	MAKE_PRIMARY_CONTACT_REQUESTED = "MAKE_PRIMARY_CONTACT_REQUESTED",
	MAKE_PRIMARY_CONTACT_SUCCESSFUL = "MAKE_PRIMARY_CONTACT_SUCCESSFUL",
	MAKE_PRIMARY_CONTACT_FAILED = "MAKE_PRIMARY_CONTACT_FAILED",

	UPDATE_CLIENT_OPPORTUNITY = "UPDATE_CLIENT_OPPORTUNITY",

	FETCH_ALL_CLIENT_VIEWS_REQUESTED = "FETCH_ALL_CLIENT_VIEWS_REQUESTED",
	FETCH_ALL_CLIENT_VIEWS_SUCCESSFUL = "FETCH_ALL_CLIENT_VIEWS_SUCCESSFUL",
	FETCH_ALL_CLIENT_VIEWS_FAILED = "FETCH_ALL_CLIENT_VIEWS_FAILED",

	FETCH_CLIENT_VIEW_METADATA_REQUESTED = "FETCH_CLIENT_VIEW_METADATA_REQUESTED",
	FETCH_CLIENT_VIEW_METADATA_SUCCESSFUL = "FETCH_CLIENT_VIEW_METADATA_SUCCESSFUL",
	FETCH_CLIENT_VIEW_METADATA_FAILED = "FETCH_CLIENT_VIEW_METADATA_FAILED",

	FETCH_CLIENT_LIST_REQUESTED = "FETCH_CLIENT_LIST_REQUESTED",
	FETCH_CLIENT_LIST_SUCCESSFUL = "FETCH_CLIENT_LIST_SUCCESSFUL",
	FETCH_CLIENT_LIST_FAILED = "FETCH_CLIENT_LIST_FAILED",

	UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW = "UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW",

	CREATE_CLIENT_VIEW_REQUESTED = "CREATE_CLIENT_VIEW_REQUESTED",
	CREATE_CLIENT_VIEW_SUCCESSFUL = "CREATE_CLIENT_VIEW_SUCCESSFUL",
	CREATE_CLIENT_VIEW_FAILED = "CREATE_CLIENT_VIEW_FAILED",

	UPDATE_CLIENT_VIEW_REQUESTED = "UPDATE_CLIENT_VIEW_REQUESTED",
	UPDATE_CLIENT_VIEW_SUCCESSFUL = "UPDATE_CLIENT_VIEW_SUCCESSFUL",
	UPDATE_CLIENT_VIEW_FAILED = "UPDATE_CLIENT_VIEW_FAILED",

	DELETE_CLIENT_VIEW_REQUESTED = "DELETE_CLIENT_VIEW_REQUESTED",
	DELETE_CLIENT_VIEW_SUCCESSFUL = "DELETE_CLIENT_VIEW_SUCCESSFUL",
	DELETE_CLIENT_VIEW_FAILED = "DELETE_CLIENT_VIEW_FAILED",

	BULK_ACTION_RECORDS_SELECTED = "BULK_ACTION_RECORDS_SELECTED",
	BULK_ACTION_RECORDS_RESET = "BULK_ACTION_RECORDS_RESET",

	BULK_DELETE_RECORDS_REQUESTED = "BULK_DELETE_RECORDS_REQUESTED",
	BULK_DELETE_RECORDS_SUCCESSFUL = "BULK_DELETE_RECORDS_SUCCESSFUL",
	BULK_DELETE_RECORDS_FAILED = "BULK_DELETE_RECORDS_FAILED",

	BULK_UPDATE_RECORDS_REQUESTED = "BULK_UPDATE_RECORDS_REQUESTED",
	BULK_UPDATE_RECORDS_SUCCESSFUL = "BULK_UPDATE_RECORDS_SUCCESSFUL",
	BULK_UPDATE_RECORDS_FAILED = "BULK_UPDATE_RECORDS_FAILED",
	RESET_CLIENTS_CURRENT_ACTIVE_VIEW = "RESET_CLIENTS_CURRENT_ACTIVE_VIEW",

	BULK_UPDATE_CLIENT_CONTACT_EMAILS_REQUESTED = "BULK_UPDATE_CLIENT_CONTACT_EMAILS_REQUESTED",
	BULK_UPDATE_CLIENT_CONTACT_EMAILS_SUCCESSFUL = "BULK_UPDATE_CLIENT_CONTACT_EMAILS_SUCCESSFUL",
	BULK_UPDATE_CLIENT_CONTACT_EMAILS_FAILED = "BULK_UPDATE_CLIENT_CONTACT_EMAILS_FAILED",

	SHIPPING_ADDRESS_DRAWER_OPENED = "SHIPPING_ADDRESS_DRAWER_OPENED",
	SHIPPING_ADDRESS_DRAWER_CLOSED = "SHIPPING_ADDRESS_DRAWER_CLOSED"
}

export type ShowClientsLoaderAction =
	Action<ClientsActionTypes.SHOW_CLIENTS_LOADER>;
export type HideClientsLoaderAction =
	Action<ClientsActionTypes.HIDE_CLIENTS_LOADER>;
export type ClientsRequestedAction =
	Action<ClientsActionTypes.CLIENTS_REQUESTED>;
export interface ClientsSuccessfulAction
	extends Action<ClientsActionTypes.CLIENTS_SUCCESSFUL> {
	readonly clientList: Client[];
	readonly clientCount: ClientCount;
}
export type ClientsFailedAction = Action<ClientsActionTypes.CLIENTS_FAILED>;
export type ClientsResetAction = Action<ClientsActionTypes.CLIENTS_RESET>;
export type ClientDetailsRequestedAction =
	Action<ClientsActionTypes.CLIENT_DETAILS_REQUESTED>;
export interface ClientDetailsSuccessfulAction
	extends Action<ClientsActionTypes.CLIENT_DETAILS_SUCCESSFUL> {
	readonly currentActiveClient: Client;
}
export type ClientDetailsFailedAction =
	Action<ClientsActionTypes.CLIENT_DETAILS_FAILED>;
export interface ClientDetailsUpdatedAction
	extends Action<ClientsActionTypes.CLIENT_DETAILS_UPDATED> {
	readonly currentActiveClient: Client;
}
export type ClientDetailsResetAction =
	Action<ClientsActionTypes.CLIENT_DETAILS_RESET>;
export type ClientContactsRequestedAction =
	Action<ClientsActionTypes.CLIENT_CONTACTS_REQUESTED>;
export interface ClientContactsSuccessfulAction
	extends Action<ClientsActionTypes.CLIENT_CONTACTS_SUCCESSFUL> {
	readonly clientContactList: ClientContact[];
}
export type ClientContactsFailedAction =
	Action<ClientsActionTypes.CLIENT_CONTACTS_FAILED>;
export type ClientContactsResetAction =
	Action<ClientsActionTypes.CLIENT_CONTACTS_RESET>;
export type DeleteClientContactRequestedAction =
	Action<ClientsActionTypes.DELETE_CLIENT_CONTACT_REQUESTED>;
export interface DeleteClientContactSuccessfulAction
	extends Action<ClientsActionTypes.DELETE_CLIENT_CONTACT_SUCCESSFUL> {
	readonly deleteClientContact: ClientContact;
}
export type DeleteClientContactFailedAction =
	Action<ClientsActionTypes.DELETE_CLIENT_CONTACT_FAILED>;
export type AddClientContactRequestedAction =
	Action<ClientsActionTypes.ADD_CLIENT_CONTACT_REQUESTED>;
export interface AddClientContactSuccessfulAction
	extends Action<ClientsActionTypes.ADD_CLIENT_CONTACT_SUCCESSFUL> {
	readonly addClientContact: ClientContact;
	readonly updatedClientContactList: ClientContact[];
}
export type AddClientContactFailedAction =
	Action<ClientsActionTypes.ADD_CLIENT_CONTACT_FAILED>;
export type EditClientContactRequestedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_CONTACT_REQUESTED>;
export interface EditClientContactSuccessfulAction
	extends Action<ClientsActionTypes.EDIT_CLIENT_CONTACT_SUCCESSFUL> {
	readonly updatedClientContactList: ClientContact[];
}
export type EditClientContactFailedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_CONTACT_FAILED>;
export type DeleteClientRequestedAction =
	Action<ClientsActionTypes.DELETE_CLIENT_REQUESTED>;
export interface DeleteClientSuccessfulAction
	extends Action<ClientsActionTypes.DELETE_CLIENT_SUCCESSFUL> {
	readonly deleteClient: Client;
}
export type DeleteClientFailedAction =
	Action<ClientsActionTypes.DELETE_CLIENT_FAILED>;
export type AddClientRequestedAction =
	Action<ClientsActionTypes.ADD_CLIENT_REQUESTED>;
export interface AddClientSuccessfulAction
	extends Action<ClientsActionTypes.ADD_CLIENT_SUCCESSFUL> {
	readonly addClient: Client;
}
export type AddClientFailedAction =
	Action<ClientsActionTypes.ADD_CLIENT_FAILED>;
export type EditClientRequestedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_REQUESTED>;
export interface EditClientSuccessfulAction
	extends Action<ClientsActionTypes.EDIT_CLIENT_SUCCESSFUL> {
	readonly editClient: Client;
}
export type EditClientFailedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_FAILED>;
export type AddClientDrawerOpenedAction =
	Action<ClientsActionTypes.ADD_CLIENT_DRAWER_OPENED>;
export type AddClientDrawerClosedAction =
	Action<ClientsActionTypes.ADD_CLIENT_DRAWER_CLOSED>;
export interface ReadonlyClientDrawerOpenedAction
	extends Action<ClientsActionTypes.READONLY_CLIENT_DRAWER_OPENED> {
	readonly clientData: Client;
}
export type ReadonlyClientDrawerClosedAction =
	Action<ClientsActionTypes.READONLY_CLIENT_DRAWER_CLOSED>;
export type EditClientDrawerOpenedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_DRAWER_OPENED>;
export type EditClientDrawerClosedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_DRAWER_CLOSED>;
export type AddClientContactDrawerOpenedAction =
	Action<ClientsActionTypes.ADD_CLIENT_CONTACT_DRAWER_OPENED>;
export type AddClientContactDrawerClosedAction =
	Action<ClientsActionTypes.ADD_CLIENT_CONTACT_DRAWER_CLOSED>;
export type EditClientContactDrawerOpenedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_CONTACT_DRAWER_OPENED>;
export type EditClientContactDrawerClosedAction =
	Action<ClientsActionTypes.EDIT_CLIENT_CONTACT_DRAWER_CLOSED>;
export type MakePrimaryContactRequestedAction =
	Action<ClientsActionTypes.MAKE_PRIMARY_CONTACT_REQUESTED>;
export interface MakePrimaryContactSuccessfulAction
	extends Action<ClientsActionTypes.MAKE_PRIMARY_CONTACT_SUCCESSFUL> {
	readonly updatedClientContactList: ClientContact[];
}
export type MakePrimaryContactFailedAction =
	Action<ClientsActionTypes.MAKE_PRIMARY_CONTACT_FAILED>;

export interface UpdateClientOpportunitiesAction
	extends Action<ClientsActionTypes.UPDATE_CLIENT_OPPORTUNITY> {
	readonly opportunities: ChildEntityOpportunities;
}

export type FetchClientViewMetadataRequested =
	Action<ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_REQUESTED>;
export interface FetchClientViewMetadataSuccessful
	extends Action<ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_SUCCESSFUL> {
	readonly metadata: CustomViewsMetadata;
}
export type FetchClientViewMetadataFailed =
	Action<ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_FAILED>;

export type FetchAllClientViewRequested =
	Action<ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_REQUESTED>;
export interface FetchAllClientViewSuccessful
	extends Action<ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_SUCCESSFUL> {
	readonly viewList: CustomViewResponse;
}
export type FetchAllClientViewFailed =
	Action<ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_FAILED>;

export type FetchClientListRequested =
	Action<ClientsActionTypes.FETCH_CLIENT_LIST_REQUESTED>;
export interface FetchClientListSuccessful
	extends Action<ClientsActionTypes.FETCH_CLIENT_LIST_SUCCESSFUL> {
	readonly clientList: ClientsResponse;
}
export type FetchClientListFailed =
	Action<ClientsActionTypes.FETCH_CLIENT_LIST_FAILED>;

export interface UpdateCurrentActiveViewAction
	extends Action<ClientsActionTypes.UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW> {
	readonly currentActiveView: CustomView;
	readonly isViewChanged: boolean;
}

export type CreateClientViewRequestedAction =
	Action<ClientsActionTypes.CREATE_CLIENT_VIEW_REQUESTED>;
export interface CreateClientViewSuccessfulAction
	extends Action<ClientsActionTypes.CREATE_CLIENT_VIEW_SUCCESSFUL> {
	readonly view: CustomView;
}
export type CreateClientViewFailedAction =
	Action<ClientsActionTypes.CREATE_CLIENT_VIEW_FAILED>;

export type UpdateClientViewRequestedAction =
	Action<ClientsActionTypes.UPDATE_CLIENT_VIEW_REQUESTED>;
export interface UpdateClientViewSuccessfulAction
	extends Action<ClientsActionTypes.UPDATE_CLIENT_VIEW_SUCCESSFUL> {
	readonly view: CustomView;
}
export type UpdateClientViewFailedAction =
	Action<ClientsActionTypes.UPDATE_CLIENT_VIEW_FAILED>;

export type DeleteClientViewRequestedAction =
	Action<ClientsActionTypes.DELETE_CLIENT_VIEW_REQUESTED>;
export interface DeleteClientViewSuccessfulAction
	extends Action<ClientsActionTypes.DELETE_CLIENT_VIEW_SUCCESSFUL> {
	readonly deletedView: CustomView;
	readonly initialView: CustomView;
}
export type DeleteClientViewFailedAction =
	Action<ClientsActionTypes.DELETE_CLIENT_VIEW_FAILED>;

export type ResetCurrentActiveViewAction =
	Action<ClientsActionTypes.RESET_CLIENTS_CURRENT_ACTIVE_VIEW>;

export type BulkActionRecordsSelectedAction =
	Action<ClientsActionTypes.BULK_ACTION_RECORDS_SELECTED>;
export type BulkActionRecordsResetAction =
	Action<ClientsActionTypes.BULK_ACTION_RECORDS_RESET>;

export type BulkDeleteRecordsRequestedAction =
	Action<ClientsActionTypes.BULK_DELETE_RECORDS_REQUESTED>;
export type BulkDeleteRecordsSuccessfulAction =
	Action<ClientsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL>;
export type BulkDeleteRecordsFailedAction =
	Action<ClientsActionTypes.BULK_DELETE_RECORDS_FAILED>;
export type BulkUpdateRecordsRequestedAction =
	Action<ClientsActionTypes.BULK_UPDATE_RECORDS_REQUESTED>;
export type BulkUpdateRecordsSuccessfulAction =
	Action<ClientsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL>;
export type BulkUpdateRecordsFailedAction =
	Action<ClientsActionTypes.BULK_UPDATE_RECORDS_FAILED>;

export type BulkUpdateClientContactEmailsRequested =
	Action<ClientsActionTypes.BULK_UPDATE_CLIENT_CONTACT_EMAILS_REQUESTED>;
export interface BulkUpdateClientContactEmailsSuccessful
	extends Action<ClientsActionTypes.BULK_UPDATE_CLIENT_CONTACT_EMAILS_SUCCESSFUL> {
	readonly results: ClientContactBulkUpdateResponse[];
}

export type BulkUpdateClientContactEmailsFailed =
	Action<ClientsActionTypes.BULK_UPDATE_CLIENT_CONTACT_EMAILS_FAILED>;

export type ShippingAddressDrawerOpenedAction =
	Action<ClientsActionTypes.SHIPPING_ADDRESS_DRAWER_OPENED>;
export type ShippingAddressDrawerClosedAction =
	Action<ClientsActionTypes.SHIPPING_ADDRESS_DRAWER_CLOSED>;

export type ClientsActions =
	| ShowClientsLoaderAction
	| HideClientsLoaderAction
	| ClientsRequestedAction
	| ClientsSuccessfulAction
	| ClientsFailedAction
	| ClientsResetAction
	| ClientDetailsRequestedAction
	| ClientDetailsSuccessfulAction
	| ClientDetailsFailedAction
	| ClientDetailsUpdatedAction
	| ClientDetailsResetAction
	| ClientContactsRequestedAction
	| ClientContactsSuccessfulAction
	| ClientContactsFailedAction
	| ClientContactsResetAction
	| DeleteClientContactRequestedAction
	| DeleteClientContactSuccessfulAction
	| DeleteClientContactFailedAction
	| AddClientContactRequestedAction
	| AddClientContactSuccessfulAction
	| AddClientContactFailedAction
	| EditClientContactRequestedAction
	| EditClientContactSuccessfulAction
	| EditClientContactFailedAction
	| DeleteClientRequestedAction
	| DeleteClientSuccessfulAction
	| DeleteClientFailedAction
	| AddClientRequestedAction
	| AddClientSuccessfulAction
	| AddClientFailedAction
	| EditClientRequestedAction
	| EditClientSuccessfulAction
	| EditClientFailedAction
	| AddClientDrawerOpenedAction
	| AddClientDrawerClosedAction
	| ReadonlyClientDrawerOpenedAction
	| ReadonlyClientDrawerClosedAction
	| EditClientDrawerOpenedAction
	| EditClientDrawerClosedAction
	| AddClientContactDrawerOpenedAction
	| AddClientContactDrawerClosedAction
	| EditClientContactDrawerOpenedAction
	| EditClientContactDrawerClosedAction
	| MakePrimaryContactSuccessfulAction
	| MakePrimaryContactFailedAction
	| UpdateClientOpportunitiesAction
	| FetchClientViewMetadataRequested
	| FetchClientViewMetadataSuccessful
	| FetchClientViewMetadataFailed
	| FetchAllClientViewRequested
	| FetchAllClientViewSuccessful
	| FetchAllClientViewFailed
	| FetchClientListRequested
	| FetchClientListSuccessful
	| FetchClientListFailed
	| UpdateCurrentActiveViewAction
	| CreateClientViewRequestedAction
	| CreateClientViewSuccessfulAction
	| CreateClientViewFailedAction
	| UpdateClientViewRequestedAction
	| UpdateClientViewSuccessfulAction
	| UpdateClientViewFailedAction
	| DeleteClientViewRequestedAction
	| DeleteClientViewSuccessfulAction
	| DeleteClientViewFailedAction
	| ResetCurrentActiveViewAction
	| BulkActionRecordsSelectedAction
	| BulkActionRecordsResetAction
	| BulkDeleteRecordsRequestedAction
	| BulkDeleteRecordsSuccessfulAction
	| BulkDeleteRecordsFailedAction
	| BulkUpdateRecordsRequestedAction
	| BulkUpdateRecordsSuccessfulAction
	| BulkUpdateRecordsFailedAction
	| BulkUpdateClientContactEmailsRequested
	| BulkUpdateClientContactEmailsSuccessful
	| BulkUpdateClientContactEmailsFailed
	| ShippingAddressDrawerOpenedAction
	| ShippingAddressDrawerClosedAction;

export interface ClientsDispatch {
	showClientsLoader: ActionCreator<
		ThunkAction<
			ShowClientsLoaderAction,
			GlobalState,
			null,
			ShowClientsLoaderAction
		>
	>;
	hideClientsLoader: ActionCreator<
		ThunkAction<
			HideClientsLoaderAction,
			GlobalState,
			null,
			HideClientsLoaderAction
		>
	>;
	getClients: ActionCreator<
		ThunkAction<
			Promise<ClientsSuccessfulAction>,
			GlobalState,
			ClientsRequest,
			ClientsSuccessfulAction
		>
	>;
	getClientsUsingGlobalSearch: ActionCreator<
		ThunkAction<
			Promise<ClientsSuccessfulAction>,
			GlobalState,
			ClientsGlobalSearchRequest,
			ClientsSuccessfulAction
		>
	>;
	resetClients: ActionCreator<
		ThunkAction<ClientsResetAction, GlobalState, null, ClientsResetAction>
	>;
	getClientDetails: ActionCreator<
		ThunkAction<
			Promise<ClientDetailsSuccessfulAction>,
			GlobalState,
			string,
			ClientDetailsSuccessfulAction
		>
	>;
	updateClientDetails: ActionCreator<
		ThunkAction<
			ClientDetailsUpdatedAction,
			GlobalState,
			Client,
			ClientDetailsUpdatedAction
		>
	>;
	resetClientDetails: ActionCreator<
		ThunkAction<
			ClientDetailsResetAction,
			GlobalState,
			null,
			ClientDetailsResetAction
		>
	>;
	getClientContacts: ActionCreator<
		ThunkAction<
			Promise<ClientContactsSuccessfulAction>,
			GlobalState,
			ClientContactRequest,
			ClientContactsSuccessfulAction
		>
	>;
	resetClientContacts: ActionCreator<
		ThunkAction<
			ClientContactsResetAction,
			GlobalState,
			null,
			ClientContactsResetAction
		>
	>;
	deleteClientContact: ActionCreator<
		ThunkAction<
			Promise<DeleteClientContactSuccessfulAction>,
			GlobalState,
			ClientContact,
			DeleteClientContactSuccessfulAction
		>
	>;
	addClientContact: ActionCreator<
		ThunkAction<
			Promise<AddClientContactSuccessfulAction>,
			GlobalState,
			AddClientContactRequest,
			AddClientContactSuccessfulAction
		>
	>;
	editClientContact: ActionCreator<
		ThunkAction<
			Promise<EditClientContactSuccessfulAction>,
			GlobalState,
			ClientContact,
			EditClientContactSuccessfulAction
		>
	>;
	deleteClient: ActionCreator<
		ThunkAction<
			Promise<DeleteClientSuccessfulAction>,
			GlobalState,
			Client,
			DeleteClientSuccessfulAction
		>
	>;
	addClient: ActionCreator<
		ThunkAction<
			Promise<AddClientSuccessfulAction>,
			GlobalState,
			AddClientRequest,
			AddClientSuccessfulAction
		>
	>;
	editClient: ActionCreator<
		ThunkAction<
			Promise<EditClientSuccessfulAction>,
			GlobalState,
			Client,
			EditClientSuccessfulAction
		>
	>;
	addClientDrawerOpened: ActionCreator<
		ThunkAction<
			AddClientDrawerOpenedAction,
			GlobalState,
			null,
			AddClientDrawerOpenedAction
		>
	>;
	addClientDrawerClosed: ActionCreator<
		ThunkAction<
			AddClientDrawerClosedAction,
			GlobalState,
			null,
			AddClientDrawerClosedAction
		>
	>;
	readonlyClientDrawerOpened: ActionCreator<
		ThunkAction<
			ReadonlyClientDrawerOpenedAction,
			GlobalState,
			Client,
			ReadonlyClientDrawerOpenedAction
		>
	>;
	readonlyClientDrawerClosed: ActionCreator<
		ThunkAction<
			ReadonlyClientDrawerClosedAction,
			GlobalState,
			null,
			ReadonlyClientDrawerClosedAction
		>
	>;
	editClientDrawerOpened: ActionCreator<
		ThunkAction<
			EditClientDrawerOpenedAction,
			GlobalState,
			null,
			EditClientDrawerOpenedAction
		>
	>;
	editClientDrawerClosed: ActionCreator<
		ThunkAction<
			EditClientDrawerClosedAction,
			GlobalState,
			null,
			EditClientDrawerClosedAction
		>
	>;
	addClientContactDrawerOpened: ActionCreator<
		ThunkAction<
			AddClientContactDrawerOpenedAction,
			GlobalState,
			null,
			AddClientContactDrawerOpenedAction
		>
	>;
	addClientContactDrawerClosed: ActionCreator<
		ThunkAction<
			AddClientContactDrawerClosedAction,
			GlobalState,
			null,
			AddClientContactDrawerClosedAction
		>
	>;
	editClientContactDrawerOpened: ActionCreator<
		ThunkAction<
			EditClientContactDrawerOpenedAction,
			GlobalState,
			null,
			EditClientContactDrawerOpenedAction
		>
	>;
	editClientContactDrawerClosed: ActionCreator<
		ThunkAction<
			EditClientContactDrawerClosedAction,
			GlobalState,
			null,
			EditClientContactDrawerClosedAction
		>
	>;
	makePrimaryContact: ActionCreator<
		ThunkAction<
			Promise<MakePrimaryContactSuccessfulAction>,
			GlobalState,
			ClientContact,
			MakePrimaryContactSuccessfulAction
		>
	>;
	updateClientOpportunity: ActionCreator<
		ThunkAction<
			UpdateClientOpportunitiesAction,
			GlobalState,
			Opportunity,
			UpdateClientOpportunitiesAction
		>
	>;
	fetchClientViewMetadata: ActionCreator<
		ThunkAction<
			Promise<FetchClientViewMetadataSuccessful>,
			GlobalState,
			null,
			FetchClientViewMetadataSuccessful
		>
	>;
	fetchAllClientView: ActionCreator<
		ThunkAction<
			Promise<FetchAllClientViewSuccessful>,
			GlobalState,
			null,
			FetchAllClientViewSuccessful
		>
	>;
	fetchAllClients: ActionCreator<
		ThunkAction<
			Promise<FetchClientListSuccessful>,
			GlobalState,
			ListPagePostRequest,
			FetchClientListSuccessful
		>
	>;
	updateCurrentActiveView: ActionCreator<
		ThunkAction<
			UpdateCurrentActiveViewAction,
			GlobalState,
			{ selectCurrentActiveView: CustomView; discardingView?: boolean },
			UpdateCurrentActiveViewAction
		>
	>;
	createClientView: ActionCreator<
		ThunkAction<
			Promise<CreateClientViewSuccessfulAction>,
			GlobalState,
			CustomView,
			CreateClientViewSuccessfulAction
		>
	>;
	updateClientView: ActionCreator<
		ThunkAction<
			Promise<UpdateClientViewSuccessfulAction>,
			GlobalState,
			CustomView,
			UpdateClientViewSuccessfulAction
		>
	>;
	deleteClientView: ActionCreator<
		ThunkAction<
			Promise<DeleteClientViewSuccessfulAction>,
			GlobalState,
			{ deleteView: CustomView; initialView: CustomView },
			DeleteClientViewSuccessfulAction
		>
	>;
	resetViews: ActionCreator<
		ThunkAction<
			ResetCurrentActiveViewAction,
			GlobalState,
			null,
			ResetCurrentActiveViewAction
		>
	>;
	bulkActionsRecordsSelected: ActionCreator<
		ThunkAction<
			BulkActionRecordsSelectedAction,
			GlobalState,
			null,
			BulkActionRecordsSelectedAction
		>
	>;
	bulkActionsRecordsReset: ActionCreator<
		ThunkAction<
			BulkActionRecordsResetAction,
			GlobalState,
			null,
			BulkActionRecordsResetAction
		>
	>;
	bulkDeleteRecords: ActionCreator<
		ThunkAction<
			Promise<BulkDeleteRecordsSuccessfulAction>,
			GlobalState,
			BulkDeleteRecordsRequest,
			BulkDeleteRecordsSuccessfulAction
		>
	>;
	bulkUpdateRecords: ActionCreator<
		ThunkAction<
			Promise<BulkUpdateRecordsSuccessfulAction>,
			GlobalState,
			BulkUpdateRecordsRequest,
			BulkUpdateRecordsSuccessfulAction
		>
	>;
	bulkUpdateClientContactEmail: ActionCreator<
		ThunkAction<
			Promise<BulkUpdateClientContactEmailsSuccessful>,
			GlobalState,
			ClientContactBulkUpdateRequest,
			BulkUpdateClientContactEmailsSuccessful
		>
	>;
	shippingAddressDrawerOpened: ActionCreator<
		ThunkAction<
			ShippingAddressDrawerOpenedAction,
			GlobalState,
			null,
			ShippingAddressDrawerOpenedAction
		>
	>;
	shippingAddressDrawerClosed: ActionCreator<
		ThunkAction<
			ShippingAddressDrawerClosedAction,
			GlobalState,
			null,
			ShippingAddressDrawerClosedAction
		>
	>;
}

export const ClientsActionCreators: ClientsDispatch = {
	showClientsLoader: () => {
		return (dispatch: Dispatch): ShowClientsLoaderAction => {
			const showClientsLoaderAction: ShowClientsLoaderAction = {
				type: ClientsActionTypes.SHOW_CLIENTS_LOADER
			};
			return dispatch(showClientsLoaderAction);
		};
	},
	hideClientsLoader: () => {
		return (dispatch: Dispatch): HideClientsLoaderAction => {
			const hideClientsLoaderAction: HideClientsLoaderAction = {
				type: ClientsActionTypes.HIDE_CLIENTS_LOADER
			};
			return dispatch(hideClientsLoaderAction);
		};
	},

	getClientsUsingGlobalSearch: (
		clientsGlobalSearchRequest: ClientsGlobalSearchRequest
	) => {
		return async (dispatch: Dispatch): Promise<ClientsSuccessfulAction> => {
			const clientsRequestedAction: ClientsRequestedAction = {
				type: ClientsActionTypes.CLIENTS_REQUESTED
			};
			dispatch(clientsRequestedAction);
			try {
				const clientsResponse = await getClientsUsingGlobalSearch(
					clientsGlobalSearchRequest
				);
				const clientsSuccessfulAction: ClientsSuccessfulAction = {
					type: ClientsActionTypes.CLIENTS_SUCCESSFUL,
					clientList: clientsResponse.data,
					clientCount: new ClientCount()
				};
				return dispatch(clientsSuccessfulAction);
			} catch (error) {
				console.error(error);
				const clientsFailedAction: ClientsFailedAction = {
					type: ClientsActionTypes.CLIENTS_FAILED
				};
				dispatch(clientsFailedAction);

				return Promise.reject(error);
			}
		};
	},

	getClients: (clientsRequest: ClientsRequest) => {
		return async (dispatch: Dispatch): Promise<ClientsSuccessfulAction> => {
			const clientsRequestedAction: ClientsRequestedAction = {
				type: ClientsActionTypes.CLIENTS_REQUESTED
			};
			dispatch(clientsRequestedAction);
			try {
				const clientsResponse = await getClients(clientsRequest);
				const clientsSuccessfulAction: ClientsSuccessfulAction = {
					type: ClientsActionTypes.CLIENTS_SUCCESSFUL,
					clientList: clientsResponse.data,
					clientCount: clientsResponse.filtered_count_map
				};
				return dispatch(clientsSuccessfulAction);
			} catch (error) {
				console.error(error);
				const clientsFailedAction: ClientsFailedAction = {
					type: ClientsActionTypes.CLIENTS_FAILED
				};
				dispatch(clientsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetClients: () => {
		return (dispatch: Dispatch): ClientsResetAction => {
			const clientsResetAction: ClientsResetAction = {
				type: ClientsActionTypes.CLIENTS_RESET
			};
			return dispatch(clientsResetAction);
		};
	},
	getClientDetails: (clientId: string, includeBlocks = false) => {
		return async (
			dispatch: Dispatch
		): Promise<ClientDetailsSuccessfulAction> => {
			const clientDetailsRequestedAction: ClientDetailsRequestedAction = {
				type: ClientsActionTypes.CLIENT_DETAILS_REQUESTED
			};
			dispatch(clientDetailsRequestedAction);
			try {
				const clientDetailsResponse = await getClientDetails(
					clientId,
					includeBlocks
				);
				const clientDetailsSuccessfulAction: ClientDetailsSuccessfulAction =
					{
						type: ClientsActionTypes.CLIENT_DETAILS_SUCCESSFUL,
						currentActiveClient: clientDetailsResponse
					};
				return dispatch(clientDetailsSuccessfulAction);
			} catch (error) {
				const clientDetailsFailedAction: ClientDetailsFailedAction = {
					type: ClientsActionTypes.CLIENT_DETAILS_FAILED
				};
				dispatch(clientDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateClientDetails: (currentActiveClient: Client) => {
		return (dispatch: Dispatch): ClientDetailsUpdatedAction => {
			const clientDetailsUpdatedAction: ClientDetailsUpdatedAction = {
				type: ClientsActionTypes.CLIENT_DETAILS_UPDATED,
				currentActiveClient
			};
			return dispatch(clientDetailsUpdatedAction);
		};
	},
	resetClientDetails: () => {
		return (dispatch: Dispatch): ClientDetailsResetAction => {
			const clientDetailsResetAction: ClientDetailsResetAction = {
				type: ClientsActionTypes.CLIENT_DETAILS_RESET
			};
			return dispatch(clientDetailsResetAction);
		};
	},
	getClientContacts: (clientContactRequest: ClientContactRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<ClientContactsSuccessfulAction> => {
			const clientContactsRequestedAction: ClientContactsRequestedAction =
				{
					type: ClientsActionTypes.CLIENT_CONTACTS_REQUESTED
				};
			dispatch(clientContactsRequestedAction);
			try {
				const clientContactsResponse = await getClientContacts(
					clientContactRequest
				);
				const clientContactsSuccessfulAction: ClientContactsSuccessfulAction =
					{
						type: ClientsActionTypes.CLIENT_CONTACTS_SUCCESSFUL,
						clientContactList: clientContactsResponse.data
					};
				return dispatch(clientContactsSuccessfulAction);
			} catch (error) {
				console.error(error);
				const clientContactsFailedAction: ClientContactsFailedAction = {
					type: ClientsActionTypes.CLIENT_CONTACTS_FAILED
				};
				dispatch(clientContactsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetClientContacts: () => {
		return (dispatch: Dispatch): ClientContactsResetAction => {
			const clientContactsResetAction: ClientContactsResetAction = {
				type: ClientsActionTypes.CLIENT_CONTACTS_RESET
			};
			return dispatch(clientContactsResetAction);
		};
	},
	deleteClientContact: (deleteClientContactRequest: ClientContact) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteClientContactSuccessfulAction> => {
			const deleteClientContactRequestedAction: DeleteClientContactRequestedAction =
				{
					type: ClientsActionTypes.DELETE_CLIENT_CONTACT_REQUESTED
				};
			dispatch(deleteClientContactRequestedAction);
			try {
				const deleteClientContactResponse = await deleteClientContact(
					deleteClientContactRequest
				);

				if (deleteClientContactResponse.deleted) {
					const deleteClientContactSuccessfulAction: DeleteClientContactSuccessfulAction =
						{
							type: ClientsActionTypes.DELETE_CLIENT_CONTACT_SUCCESSFUL,
							deleteClientContact: deleteClientContactRequest
						};
					return dispatch(deleteClientContactSuccessfulAction);
				} else {
					const deleteClientContactFailedAction: DeleteClientContactFailedAction =
						{
							type: ClientsActionTypes.DELETE_CLIENT_CONTACT_FAILED
						};
					dispatch(deleteClientContactFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const deleteClientContactFailedAction: DeleteClientContactFailedAction =
					{
						type: ClientsActionTypes.DELETE_CLIENT_CONTACT_FAILED
					};
				dispatch(deleteClientContactFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addClientContact: (addClientContactRequest: AddClientContactRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddClientContactSuccessfulAction> => {
			const addClientContactRequestedAction: AddClientContactRequestedAction =
				{
					type: ClientsActionTypes.ADD_CLIENT_CONTACT_REQUESTED
				};
			dispatch(addClientContactRequestedAction);
			try {
				const addClientContactResponse = await addClientContact(
					addClientContactRequest
				);
				const clientContactRequest: ClientContactRequest = {
					filters: new Array<TableQueryFilter>(),
					page: 1,
					limit: 100,
					clientId: addClientContactResponse.client_company_id
				};
				const updatedContactListResponse = await getClientContacts(
					clientContactRequest
				);
				const addClientContactSuccessfulAction: AddClientContactSuccessfulAction =
					{
						type: ClientsActionTypes.ADD_CLIENT_CONTACT_SUCCESSFUL,
						addClientContact: addClientContactResponse,
						updatedClientContactList:
							updatedContactListResponse.data
					};
				return dispatch(addClientContactSuccessfulAction);
			} catch (error) {
				const addClientContactFailedAction: AddClientContactFailedAction =
					{
						type: ClientsActionTypes.ADD_CLIENT_CONTACT_FAILED
					};
				dispatch(addClientContactFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editClientContact: (editClientContactRequest: ClientContact) => {
		return async (
			dispatch: Dispatch
		): Promise<EditClientContactSuccessfulAction> => {
			const editClientContactRequestedAction: EditClientContactRequestedAction =
				{
					type: ClientsActionTypes.EDIT_CLIENT_CONTACT_REQUESTED
				};
			dispatch(editClientContactRequestedAction);
			try {
				const editClientContactResponse = await editClientContact(
					editClientContactRequest
				);
				const clientContactRequest: ClientContactRequest = {
					filters: new Array<TableQueryFilter>(),
					page: 1,
					limit: 100,
					clientId: editClientContactResponse.client_company_id
				};
				const updatedContactList = await getClientContacts(
					clientContactRequest
				);

				const editClientContactSuccessfulAction: EditClientContactSuccessfulAction =
					{
						type: ClientsActionTypes.EDIT_CLIENT_CONTACT_SUCCESSFUL,
						updatedClientContactList: updatedContactList.data
					};
				return dispatch(editClientContactSuccessfulAction);
			} catch (error) {
				const editClientContactFailedAction: EditClientContactFailedAction =
					{
						type: ClientsActionTypes.EDIT_CLIENT_CONTACT_FAILED
					};
				dispatch(editClientContactFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteClient: (deleteClientRequest: Client) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteClientSuccessfulAction> => {
			const deleteClientRequestedAction: DeleteClientRequestedAction = {
				type: ClientsActionTypes.DELETE_CLIENT_REQUESTED
			};
			dispatch(deleteClientRequestedAction);
			try {
				const deleteClientResponse = await deleteClient(
					deleteClientRequest
				);
				if (deleteClientResponse.deleted) {
					const deleteClientSuccessfulAction: DeleteClientSuccessfulAction =
						{
							type: ClientsActionTypes.DELETE_CLIENT_SUCCESSFUL,
							deleteClient: deleteClientRequest
						};
					return dispatch(deleteClientSuccessfulAction);
				} else {
					const deleteClientFailedAction: DeleteClientFailedAction = {
						type: ClientsActionTypes.DELETE_CLIENT_FAILED
					};
					dispatch(deleteClientFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const deleteClientFailedAction: DeleteClientFailedAction = {
					type: ClientsActionTypes.DELETE_CLIENT_FAILED
				};
				dispatch(deleteClientFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addClient: (addClientRequest: AddClientRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddClientSuccessfulAction> => {
			const addClientRequestedAction: AddClientRequestedAction = {
				type: ClientsActionTypes.ADD_CLIENT_REQUESTED
			};
			dispatch(addClientRequestedAction);
			try {
				const addClientResponse = await addClient(addClientRequest);
				const addClientSuccessfulAction: AddClientSuccessfulAction = {
					type: ClientsActionTypes.ADD_CLIENT_SUCCESSFUL,
					addClient: addClientResponse
				};
				return dispatch(addClientSuccessfulAction);
			} catch (error) {
				const addClientFailedAction: AddClientFailedAction = {
					type: ClientsActionTypes.ADD_CLIENT_FAILED
				};
				dispatch(addClientFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editClient: (editClientRequest: EditClientRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<EditClientSuccessfulAction> => {
			const editClientRequestedAction: EditClientRequestedAction = {
				type: ClientsActionTypes.EDIT_CLIENT_REQUESTED
			};
			dispatch(editClientRequestedAction);
			try {
				const editClientResponse = await editClient(editClientRequest);
				const editClientSuccessfulAction: EditClientSuccessfulAction = {
					type: ClientsActionTypes.EDIT_CLIENT_SUCCESSFUL,
					editClient: editClientResponse
				};
				return dispatch(editClientSuccessfulAction);
			} catch (error) {
				const editClientFailedAction: EditClientFailedAction = {
					type: ClientsActionTypes.EDIT_CLIENT_FAILED
				};
				dispatch(editClientFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addClientDrawerOpened: () => {
		return (dispatch: Dispatch): AddClientDrawerOpenedAction => {
			const addClientDrawerOpenedAction: AddClientDrawerOpenedAction = {
				type: ClientsActionTypes.ADD_CLIENT_DRAWER_OPENED
			};
			return dispatch(addClientDrawerOpenedAction);
		};
	},
	addClientDrawerClosed: () => {
		return (dispatch: Dispatch): AddClientDrawerClosedAction => {
			const addClientDrawerClosedAction: AddClientDrawerClosedAction = {
				type: ClientsActionTypes.ADD_CLIENT_DRAWER_CLOSED
			};
			return dispatch(addClientDrawerClosedAction);
		};
	},
	readonlyClientDrawerOpened: (clientData: Client) => {
		return (dispatch: Dispatch): ReadonlyClientDrawerOpenedAction => {
			const readonlyClientDrawerOpenedAction: ReadonlyClientDrawerOpenedAction =
				{
					type: ClientsActionTypes.READONLY_CLIENT_DRAWER_OPENED,
					clientData
				};
			return dispatch(readonlyClientDrawerOpenedAction);
		};
	},
	readonlyClientDrawerClosed: () => {
		return (dispatch: Dispatch): ReadonlyClientDrawerClosedAction => {
			const readonlyClientDrawerClosedAction: ReadonlyClientDrawerClosedAction =
				{
					type: ClientsActionTypes.READONLY_CLIENT_DRAWER_CLOSED
				};
			return dispatch(readonlyClientDrawerClosedAction);
		};
	},
	editClientDrawerOpened: () => {
		return (dispatch: Dispatch): EditClientDrawerOpenedAction => {
			const editClientDrawerOpenedAction: EditClientDrawerOpenedAction = {
				type: ClientsActionTypes.EDIT_CLIENT_DRAWER_OPENED
			};
			return dispatch(editClientDrawerOpenedAction);
		};
	},
	editClientDrawerClosed: () => {
		return (dispatch: Dispatch): EditClientDrawerClosedAction => {
			const editClientDrawerClosedAction: EditClientDrawerClosedAction = {
				type: ClientsActionTypes.EDIT_CLIENT_DRAWER_CLOSED
			};
			return dispatch(editClientDrawerClosedAction);
		};
	},
	addClientContactDrawerOpened: () => {
		return (dispatch: Dispatch): AddClientContactDrawerOpenedAction => {
			const addClientContactDrawerOpenedAction: AddClientContactDrawerOpenedAction =
				{
					type: ClientsActionTypes.ADD_CLIENT_CONTACT_DRAWER_OPENED
				};
			return dispatch(addClientContactDrawerOpenedAction);
		};
	},
	addClientContactDrawerClosed: () => {
		return (dispatch: Dispatch): AddClientContactDrawerClosedAction => {
			const addClientContactDrawerClosedAction: AddClientContactDrawerClosedAction =
				{
					type: ClientsActionTypes.ADD_CLIENT_CONTACT_DRAWER_CLOSED
				};
			return dispatch(addClientContactDrawerClosedAction);
		};
	},
	editClientContactDrawerOpened: () => {
		return (dispatch: Dispatch): EditClientContactDrawerOpenedAction => {
			const editClientContactDrawerOpenedAction: EditClientContactDrawerOpenedAction =
				{
					type: ClientsActionTypes.EDIT_CLIENT_CONTACT_DRAWER_OPENED
				};
			return dispatch(editClientContactDrawerOpenedAction);
		};
	},
	editClientContactDrawerClosed: () => {
		return (dispatch: Dispatch): EditClientContactDrawerClosedAction => {
			const editClientContactDrawerClosedAction: EditClientContactDrawerClosedAction =
				{
					type: ClientsActionTypes.EDIT_CLIENT_CONTACT_DRAWER_CLOSED
				};
			return dispatch(editClientContactDrawerClosedAction);
		};
	},
	makePrimaryContact: (makePrimaryContactRequest: ClientContact) => {
		return async (
			dispatch: Dispatch
		): Promise<MakePrimaryContactSuccessfulAction> => {
			const makePrimaryContactRequestedAction: MakePrimaryContactRequestedAction =
				{
					type: ClientsActionTypes.MAKE_PRIMARY_CONTACT_REQUESTED
				};
			dispatch(makePrimaryContactRequestedAction);
			try {
				makePrimaryContactRequest.contact_type =
					ClientUserContactType.PrimaryContact;
				const makePrimaryContactResponse = await editClientContact(
					makePrimaryContactRequest
				);
				const clientContactRequest: ClientContactRequest = {
					filters: new Array<TableQueryFilter>(),
					page: 1,
					limit: 100,
					clientId: makePrimaryContactResponse.client_company_id
				};
				const updatedClientContactList = await getClientContacts(
					clientContactRequest
				);
				const makePrimaryContactSuccessfulAction: MakePrimaryContactSuccessfulAction =
					{
						type: ClientsActionTypes.MAKE_PRIMARY_CONTACT_SUCCESSFUL,
						updatedClientContactList: updatedClientContactList.data
					};
				return dispatch(makePrimaryContactSuccessfulAction);
			} catch (error) {
				const makePrimaryContactFailedAction: MakePrimaryContactFailedAction =
					{
						type: ClientsActionTypes.MAKE_PRIMARY_CONTACT_FAILED
					};
				dispatch(makePrimaryContactFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateClientOpportunity: (opportunity: Opportunity) => {
		return (
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateClientOpportunitiesAction => {
			const oldData: ChildEntityOpportunities | null =
				getState().clients.currentActiveClient?.opportunities ?? null;

			const updateClientOpportunity: ChildEntityOpportunities = oldData
				? {
						data: oldData.data.concat(opportunity)
				  }
				: {
						data: [opportunity]
				  };

			const updateClientOpportunityAction: UpdateClientOpportunitiesAction =
				{
					type: ClientsActionTypes.UPDATE_CLIENT_OPPORTUNITY,
					opportunities: updateClientOpportunity
				};
			return dispatch(updateClientOpportunityAction);
		};
	},
	fetchClientViewMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchClientViewMetadataSuccessful> => {
			const fetchViewMetadataRequested: FetchClientViewMetadataRequested =
				{
					type: ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_REQUESTED
				};

			dispatch(fetchViewMetadataRequested);

			try {
				const response = await getClientViewMetadata();
				const fetchViewMetadataSuccessful: FetchClientViewMetadataSuccessful =
					{
						type: ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_SUCCESSFUL,
						metadata: response
					};
				return dispatch(fetchViewMetadataSuccessful);
			} catch (error) {
				console.error(error);

				const fetchViewMetadataFailed: FetchClientViewMetadataFailed = {
					type: ClientsActionTypes.FETCH_CLIENT_VIEW_METADATA_FAILED
				};
				dispatch(fetchViewMetadataFailed);

				return Promise.reject(error);
			}
		};
	},
	fetchAllClientView: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchAllClientViewSuccessful> => {
			const fetchAllClientViewRequested: FetchAllClientViewRequested = {
				type: ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_REQUESTED
			};

			dispatch(fetchAllClientViewRequested);

			try {
				const response = await getAllClientViews();
				const fetchAllClientViewSuccessful: FetchAllClientViewSuccessful =
					{
						type: ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_SUCCESSFUL,
						viewList: response
					};
				return dispatch(fetchAllClientViewSuccessful);
			} catch (error) {
				console.error(error);

				const fetchAllClientViewFailed: FetchAllClientViewFailed = {
					type: ClientsActionTypes.FETCH_ALL_CLIENT_VIEWS_FAILED
				};
				dispatch(fetchAllClientViewFailed);

				return Promise.reject(error);
			}
		};
	},
	fetchAllClients: (clientRequest: ListPagePostRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<FetchClientListSuccessful> => {
			const fetchClientListRequested: FetchClientListRequested = {
				type: ClientsActionTypes.FETCH_CLIENT_LIST_REQUESTED
			};
			dispatch(fetchClientListRequested);
			try {
				const response = await getClientListPost(clientRequest);

				const fetchClientListSuccessful: FetchClientListSuccessful = {
					type: ClientsActionTypes.FETCH_CLIENT_LIST_SUCCESSFUL,
					clientList: response
				};

				return dispatch(fetchClientListSuccessful);
			} catch (error) {
				const fetchClientListFailed: FetchClientListFailed = {
					type: ClientsActionTypes.FETCH_CLIENT_LIST_FAILED
				};

				dispatch(fetchClientListFailed);

				return Promise.reject(error);
			}
		};
	},
	updateCurrentActiveView: (
		selectedCurrentActiveView: CustomView,
		discardingView?: boolean
	) => {
		return (
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateCurrentActiveViewAction => {
			const currentActiveView = getState().clients.currentActiveView;
			const prevViewData = getState().clients.viewList?.data.find(
				view => view.id === selectedCurrentActiveView?.id
			);

			const isSameView =
				currentActiveView?.id === selectedCurrentActiveView?.id;

			if (isSameView) {
				// if its a custom view then it would have an id
				if (prevViewData?.id) {
					const hasFiltersChanged = areFiltersChanged(
						prevViewData.filter_criteria,
						selectedCurrentActiveView.filter_criteria
					);

					const hasColumnsChanged =
						JSON.stringify(selectedCurrentActiveView.columns) !==
						JSON.stringify(prevViewData.columns);

					const updateCurrentActiveViewAction: UpdateCurrentActiveViewAction =
						{
							type: ClientsActionTypes.UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW,
							currentActiveView: selectedCurrentActiveView,
							isViewChanged: discardingView
								? false
								: hasFiltersChanged || hasColumnsChanged
						};
					return dispatch(updateCurrentActiveViewAction);
				}

				// if its a system view then no id

				if (currentActiveView) {
					const hasFiltersChanged = areFiltersChanged(
						currentActiveView.filter_criteria,
						selectedCurrentActiveView.filter_criteria
					);

					const hasColumnsChanged =
						JSON.stringify(selectedCurrentActiveView.columns) !==
						JSON.stringify(currentActiveView?.columns);

					const updateCurrentActiveViewAction: UpdateCurrentActiveViewAction =
						{
							type: ClientsActionTypes.UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW,
							currentActiveView: selectedCurrentActiveView,
							isViewChanged: discardingView
								? false
								: hasFiltersChanged || hasColumnsChanged
						};
					return dispatch(updateCurrentActiveViewAction);
				}
			}

			const updateCurrentActiveViewAction: UpdateCurrentActiveViewAction =
				{
					type: ClientsActionTypes.UPDATE_CLIENTS_CURRENT_ACTIVE_VIEW,
					currentActiveView: selectedCurrentActiveView,
					isViewChanged: discardingView ? true : false
				};
			return dispatch(updateCurrentActiveViewAction);
		};
	},
	createClientView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<CreateClientViewSuccessfulAction> => {
			const createClientViewRequestedAction: CreateClientViewRequestedAction =
				{
					type: ClientsActionTypes.CREATE_CLIENT_VIEW_REQUESTED
				};

			dispatch(createClientViewRequestedAction);

			try {
				const response = await createClientView(view);
				const createClientViewSuccessfulAction: CreateClientViewSuccessfulAction =
					{
						type: ClientsActionTypes.CREATE_CLIENT_VIEW_SUCCESSFUL,
						view: response
					};
				return dispatch(createClientViewSuccessfulAction);
			} catch (error) {
				const createClientViewFailedAction: CreateClientViewFailedAction =
					{
						type: ClientsActionTypes.CREATE_CLIENT_VIEW_FAILED
					};
				dispatch(createClientViewFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateClientView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateClientViewSuccessfulAction> => {
			const updateClientViewRequestedAction: UpdateClientViewRequestedAction =
				{
					type: ClientsActionTypes.UPDATE_CLIENT_VIEW_REQUESTED
				};

			dispatch(updateClientViewRequestedAction);

			try {
				const response = await updateClientView(view);
				const updateClientViewSuccessfulAction: UpdateClientViewSuccessfulAction =
					{
						type: ClientsActionTypes.UPDATE_CLIENT_VIEW_SUCCESSFUL,
						view: response
					};
				return dispatch(updateClientViewSuccessfulAction);
			} catch (error) {
				const updateClientViewFailedAction: UpdateClientViewFailedAction =
					{
						type: ClientsActionTypes.UPDATE_CLIENT_VIEW_FAILED
					};
				dispatch(updateClientViewFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteClientView: (deleteView: CustomView, initialView: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteClientViewSuccessfulAction> => {
			const deleteClientViewRequestedAction: DeleteClientViewRequestedAction =
				{
					type: ClientsActionTypes.DELETE_CLIENT_VIEW_REQUESTED
				};

			dispatch(deleteClientViewRequestedAction);

			try {
				await deleteClientView(deleteView.id);
				const deleteClientViewSuccessfulAction: DeleteClientViewSuccessfulAction =
					{
						type: ClientsActionTypes.DELETE_CLIENT_VIEW_SUCCESSFUL,
						deletedView: deleteView,
						initialView: initialView
					};
				return dispatch(deleteClientViewSuccessfulAction);
			} catch (error) {
				const deleteClientViewFailedAction: DeleteClientViewFailedAction =
					{
						type: ClientsActionTypes.DELETE_CLIENT_VIEW_FAILED
					};
				dispatch(deleteClientViewFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetViews: () => {
		return (dispatch: Dispatch): ResetCurrentActiveViewAction => {
			const resetCurrentActiveViewAction: ResetCurrentActiveViewAction = {
				type: ClientsActionTypes.RESET_CLIENTS_CURRENT_ACTIVE_VIEW
			};

			return dispatch(resetCurrentActiveViewAction);
		};
	},
	bulkActionsRecordsSelected: () => {
		return (dispatch: Dispatch): BulkActionRecordsSelectedAction => {
			const bulkActionRecordsSelectedAction: BulkActionRecordsSelectedAction =
				{
					type: ClientsActionTypes.BULK_ACTION_RECORDS_SELECTED
				};

			return dispatch(bulkActionRecordsSelectedAction);
		};
	},
	bulkActionsRecordsReset: () => {
		return (dispatch: Dispatch): BulkActionRecordsResetAction => {
			const bulkActionRecordsResetAction: BulkActionRecordsResetAction = {
				type: ClientsActionTypes.BULK_ACTION_RECORDS_RESET
			};

			return dispatch(bulkActionRecordsResetAction);
		};
	},
	bulkDeleteRecords: (deleteRecordsRequest: BulkDeleteRecordsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkDeleteRecordsSuccessfulAction> => {
			const bulkDeleteRecordsRequestedAction: BulkDeleteRecordsRequestedAction =
				{
					type: ClientsActionTypes.BULK_DELETE_RECORDS_REQUESTED
				};

			dispatch(bulkDeleteRecordsRequestedAction);

			try {
				await bulkDeleteClients(deleteRecordsRequest);

				const bulkDeleteRecordsSuccessfulAction: BulkDeleteRecordsSuccessfulAction =
					{
						type: ClientsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL
					};

				return dispatch(bulkDeleteRecordsSuccessfulAction);
			} catch (error) {
				const bulkDeleteRecordsFailedAction: BulkDeleteRecordsFailedAction =
					{
						type: ClientsActionTypes.BULK_DELETE_RECORDS_FAILED
					};
				dispatch(bulkDeleteRecordsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	bulkUpdateRecords: (updateRecordsRequest: BulkUpdateRecordsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkUpdateRecordsSuccessfulAction> => {
			const bulkUpdateRecordsRequestedAction: BulkUpdateRecordsRequestedAction =
				{
					type: ClientsActionTypes.BULK_UPDATE_RECORDS_REQUESTED
				};
			dispatch(bulkUpdateRecordsRequestedAction);
			try {
				await bulkUpdateClients(updateRecordsRequest);
				const bulkUpdateRecordsSuccessfulAction: BulkUpdateRecordsSuccessfulAction =
					{
						type: ClientsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL
					};
				return dispatch(bulkUpdateRecordsSuccessfulAction);
			} catch (error) {
				const bulkUpdateRecordsFailedAction: BulkUpdateRecordsFailedAction =
					{
						type: ClientsActionTypes.BULK_UPDATE_RECORDS_FAILED
					};
				dispatch(bulkUpdateRecordsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	bulkUpdateClientContactEmail: (
		bulkUpdateRequest: ClientContactBulkUpdateRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkUpdateClientContactEmailsSuccessful> => {
			const bulkUpdateClientContactEmailsRequest: BulkUpdateClientContactEmailsRequested =
				{
					type: ClientsActionTypes.BULK_UPDATE_CLIENT_CONTACT_EMAILS_REQUESTED
				};
			dispatch(bulkUpdateClientContactEmailsRequest);
			try {
				const res: ClientContactBulkUpdateServiceResponse =
					await bulkUpdateClientContact(bulkUpdateRequest);
				const bulkUpdateSuccess: BulkUpdateClientContactEmailsSuccessful =
					{
						type: ClientsActionTypes.BULK_UPDATE_CLIENT_CONTACT_EMAILS_SUCCESSFUL,
						results: res.results
					};
				return dispatch(bulkUpdateSuccess);
			} catch (err) {
				const bulkUpdateFailed: BulkUpdateClientContactEmailsFailed = {
					type: ClientsActionTypes.BULK_UPDATE_CLIENT_CONTACT_EMAILS_FAILED
				};
				dispatch(bulkUpdateFailed);
				return Promise.reject(err);
			}
		};
	},
	shippingAddressDrawerOpened: () => {
		return (dispatch: Dispatch): ShippingAddressDrawerOpenedAction => {
			const shippingAddressDrawerOpenedAction: ShippingAddressDrawerOpenedAction =
				{
					type: ClientsActionTypes.SHIPPING_ADDRESS_DRAWER_OPENED
				};
			return dispatch(shippingAddressDrawerOpenedAction);
		};
	},
	shippingAddressDrawerClosed: () => {
		return (dispatch: Dispatch): ShippingAddressDrawerClosedAction => {
			const shippingAddressDrawerClosedAction: ShippingAddressDrawerClosedAction =
				{
					type: ClientsActionTypes.SHIPPING_ADDRESS_DRAWER_CLOSED
				};
			return dispatch(shippingAddressDrawerClosedAction);
		};
	}
};

export default ClientsActionCreators;
